<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import BrandHeader from './BrandHeader.svelte';
  import {
    playCopySound,
    playCountdownTick,
    playCriticalBeep,
    playFinalCountdown,
    playReturnSound,
    playUrgencyPulse,
    playWarningBeep,
  } from './sounds';
  import type { BrandImage, CopyBox, GameOption } from './types';
  import { darkenColor, hexToRgb, lightenColor } from './utils';

  export let selectedOption: GameOption;
  export let offerScreenKickerText: string | undefined;
  export let offerScreenTitleText: string;
  export let offerScreenReturnText: string;
  export let offerScreenCountdownSeconds: number;
  export let copyBox: CopyBox;
  export let image: BrandImage;
  export let cardTitle: string | undefined = undefined;

  const dispatch = createEventDispatcher<{ return: void }>();

  // Store countdown in milliseconds for smooth animation
  let countdownMs = offerScreenCountdownSeconds * 1000;
  let copied = false;
  let showContent = false;
  let root: HTMLElement;

  // Track timeouts and interval for cleanup
  let timeouts: ReturnType<typeof setTimeout>[] = [];
  let countdownInterval: ReturnType<typeof setInterval> | null = null;

  // Urgency level based on seconds remaining
  $: secondsRemaining = Math.ceil(countdownMs / 1000);
  $: urgencyLevel = secondsRemaining <= 5 ? 2 : secondsRemaining <= 10 ? 1 : 0;

  // Progress bar percentage (0% at start, 100% when countdown reaches 0)
  $: initialMs = offerScreenCountdownSeconds * 1000;
  $: progressPercent = initialMs > 0 ? ((initialMs - countdownMs) / initialMs) * 100 : 0;

  // Replace $[[game.name]] placeholder with actual game title
  $: formattedTitle = offerScreenTitleText.replace('$[[game.name]]', selectedOption.title);

  // Use linkTrackingLink if provided, otherwise fall back to link
  $: targetLink = selectedOption.linkTrackingLink ?? selectedOption.link;

  // Format time with milliseconds: MM:SS:mmm
  function formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const milliseconds = ms % 1000;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`;
  }

  function handleReturn() {
    playReturnSound();
    dispatch('return');
  }

  async function handleCopyAndVisit(event: MouseEvent) {
    const clickedElement = event.currentTarget as HTMLElement;

    // Play copy sound
    playCopySound();

    // Stop the countdown timer
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }

    try {
      await navigator.clipboard.writeText(copyBox.copyText);
      copied = true;
      timeouts.push(
        setTimeout(() => {
          copied = false;
        }, 2000)
      );
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = copyBox.copyText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      copied = true;
      timeouts.push(
        setTimeout(() => {
          copied = false;
        }, 2000)
      );
    }

    window?.triggerNativeClickEvent?.(clickedElement, targetLink, copyBox.buttonText, { option: selectedOption.title });

    // Open link in new tab
    window.open(targetLink, '_blank', 'noopener,noreferrer');
  }

  onMount(() => {
    window?.triggerNativeInteractionEvent?.(root, 'offer_shown', {
      option: selectedOption.title,
    });

    // Entrance animation
    timeouts.push(
      setTimeout(() => {
        showContent = true;
      }, 100)
    );

    if (offerScreenCountdownSeconds <= 0) return;

    const startTime = Date.now();
    const initialMs = offerScreenCountdownSeconds * 1000;
    let lastSecond = Math.ceil(initialMs / 1000);
    let warningPlayed = false;
    let criticalPlayed = false;

    // Update every 50ms for smooth millisecond display
    countdownInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      countdownMs = Math.max(initialMs - elapsed, 0);
      const currentSecond = Math.ceil(countdownMs / 1000);

      // Play sounds on each second change
      if (currentSecond !== lastSecond && currentSecond > 0) {
        lastSecond = currentSecond;

        // Critical countdown (last 5 seconds) - beep every second
        if (currentSecond <= 5) {
          playCriticalBeep();
          if (currentSecond === 3) {
            playFinalCountdown();
          }
        }
        // Warning zone (10-6 seconds) - beep at transitions
        else if (currentSecond <= 10) {
          if (!criticalPlayed && currentSecond === 5) {
            playCriticalBeep();
            criticalPlayed = true;
          } else if (currentSecond === 10 && !warningPlayed) {
            playWarningBeep();
            warningPlayed = true;
          } else {
            playCountdownTick();
          }
        }
        // Normal countdown - subtle ticks every 5 seconds
        else if (currentSecond % 5 === 0) {
          playCountdownTick();
        }
      }

      // Urgency pulse during critical phase
      if (currentSecond <= 5 && countdownMs % 500 < 60) {
        playUrgencyPulse();
      }

      if (countdownMs <= 0) {
        if (countdownInterval) {
          clearInterval(countdownInterval);
          countdownInterval = null;
        }
        playFinalCountdown();
        dispatch('return');
      }
    }, 50);

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      timeouts.forEach((t) => clearTimeout(t));
    };
  });
</script>

<div
  class="offer-screen"
  bind:this={root}
  class:urgency-warning={urgencyLevel === 1}
  class:urgency-critical={urgencyLevel === 2}
  style="--bg-color: {selectedOption.backgroundHex}; --bg-color-dark: {darkenColor(
    selectedOption.backgroundHex,
    25
  )}; --bg-color-light: {lightenColor(selectedOption.backgroundHex, 15)}; --bg-color-rgb: {hexToRgb(selectedOption.backgroundHex)};"
>
  <!-- Background pattern with screen blend mode -->
  <div class="bg-pattern" aria-hidden="true"></div>

  <!-- Animated background -->
  <div class="bg-animation" aria-hidden="true"></div>

  <!-- Critical urgency vignette overlay -->
  {#if urgencyLevel === 2}
    <div class="urgency-vignette" aria-hidden="true"></div>
  {/if}

  <BrandHeader {image} {cardTitle} backgroundHex={selectedOption.backgroundHex} />

  <div class="offer-content" class:show={showContent}>
    <div class="offer-header">
      <span class="game-emoji" aria-hidden="true">{selectedOption.emoji}</span>
      {#if offerScreenKickerText}
        <span class="kicker-text">{offerScreenKickerText}</span>
      {/if}
      <h2 class="offer-title">{formattedTitle}</h2>
    </div>

    <div class="copy-section">
      {#if offerScreenCountdownSeconds > 0}
        <span
          class="countdown-time"
          class:warning={urgencyLevel === 1}
          class:critical={urgencyLevel === 2}
          role="timer"
          aria-label="Time remaining: {formatTime(countdownMs)}">{formatTime(countdownMs)}</span
        >
      {/if}

      <div class="coupon-wrapper" role="group" aria-label="Promo code">
        <!-- Left cutout -->
        <div class="coupon-cutout coupon-cutout-left" aria-hidden="true"></div>

        <button class="coupon-code" on:click={handleCopyAndVisit} aria-label="Click to copy code and visit site">
          <span class="code-label">YOUR CODE</span>
          <span class="code-text">{copyBox.copyText}</span>
          <div class="code-shimmer" aria-hidden="true"></div>
        </button>

        <!-- Right cutout -->
        <div class="coupon-cutout coupon-cutout-right" aria-hidden="true"></div>

        <button
          class="copy-button"
          on:click={handleCopyAndVisit}
          aria-label={copied ? 'Code copied! Click to visit site' : `${copyBox.buttonText} and visit site`}
        >
          <div class="button-glow" aria-hidden="true"></div>
          {#if copied}
            <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          {:else}
            <svg class="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          {/if}
          <span>{copied ? 'Copied!' : copyBox.buttonText}</span>
          <svg class="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
      </div>
    </div>
    <button class="return-link" on:click={handleReturn}>
      <i class="fas fa-long-arrow-left"></i>
      {offerScreenReturnText}
    </button>
  </div>

  <!-- Progress bar at bottom -->
  {#if offerScreenCountdownSeconds > 0}
    <div
      class="progress-bar-container"
      class:warning={progressPercent >= 50 && progressPercent < 80}
      class:critical={progressPercent >= 80}
      aria-hidden="true"
    >
      <div class="progress-bar-fill" style="width: {progressPercent}%"></div>
    </div>
  {/if}
</div>

<style>
  @media (min-width: 769px) {
    :global(.guided-offer-holder[data-style='banner'] .return-link i) {
      display: inline-block !important;
      margin-right: 5px !important;
    }

    :global(.guided-offer-holder[data-style='banner'] .countdown-time.countdown-time) {
      margin-bottom: 6px !important;
    }
    :global(.guided-offer-holder[data-style='banner'] .return-link.return-link.return-link.return-link) {
      position: absolute !important;
      top: 15px !important;
      left: 15px !important;
      font-size: 1rem !important;
      width: 130px !important;
      height: 20px;
    }
  }

  @media (min-width: 1201px) {
    :global(.guided-offer-holder[data-style='banner'] .coupon-wrapper.coupon-wrapper) {
      flex-direction: row !important;
    }

    :global(.guided-offer-holder[data-style='banner'] .coupon-code.coupon-code.coupon-code) {
      border-radius: 10px 0 0 10px !important;
      border-bottom: 2px dashed rgba(255, 215, 0, 0.5) !important;
      border-right: 0 !important;
    }
    :global(.guided-offer-holder[data-style='banner'] .copy-button.copy-button.copy-button) {
      border-radius: 0 10px 10px 0 !important;
    }
  }

  .offer-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 12px 24px;
    box-sizing: border-box;
    background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    position: relative;
    overflow: hidden;
    container-type: size;
    container-name: offer-screen;
  }

  .bg-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: 75% 50%;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
    opacity: 0.12;
    pointer-events: none;
  }

  /* Animated background gradient */
  .bg-animation {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, var(--bg-color-light) 0%, transparent 50%);
    animation: bgFloat 8s ease-in-out infinite;
    opacity: 0.2;
    pointer-events: none;
  }

  @keyframes bgFloat {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    25% {
      transform: translate(10%, 5%) scale(1.1);
    }
    50% {
      transform: translate(5%, 10%) scale(1);
    }
    75% {
      transform: translate(-5%, 5%) scale(1.1);
    }
  }

  .offer-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    max-width: 500px;
    position: relative;
    z-index: 1;
  }

  /* Staggered entrance animations */
  .offer-content .offer-header {
    opacity: 0;
    transform: translateY(20px);
    transition:
      opacity 0.4s ease 0.1s,
      transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.1s;
  }

  .offer-content .copy-section {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
    transition:
      opacity 0.4s ease 0.25s,
      transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.25s;
  }

  .offer-content.show .offer-header {
    opacity: 1;
    transform: translateY(0);
  }

  .offer-content.show .copy-section {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .offer-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
  }

  .game-emoji {
    font-size: 32px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
    animation: emojiFloat 2s ease-in-out infinite;
  }

  @keyframes emojiFloat {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  .kicker-text {
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
    text-transform: uppercase;
    letter-spacing: 2px;
    background: linear-gradient(90deg, #fff, #ffd700, #fff);
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shimmerText 3s linear infinite;
  }

  @keyframes shimmerText {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  .offer-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: white;
  }

  .copy-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex-grow: 1;
    justify-content: space-between;
  }

  .coupon-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    position: relative;
  }

  .coupon-code {
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 9.600000000000001px 24px;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%);
    border: 2px dashed rgba(255, 215, 0, 0.5);
    border-right: none;
    border-radius: 8px 0 0 8px;
    overflow: hidden;
    backdrop-filter: blur(10px);
    cursor: pointer;
    transition:
      background 0.2s ease,
      border-color 0.2s ease;
  }

  .coupon-code:hover {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.7) 100%);
    border-color: rgba(255, 215, 0, 0.7);
  }

  .coupon-code:active {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.8) 100%);
  }

  .coupon-cutout {
    position: absolute;
    width: 16px;
    height: 16px;
    background: var(--bg-color-dark);
    border-radius: 50%;
    z-index: 2;
  }

  .coupon-cutout-left {
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    box-shadow: inset 2px 0 4px rgba(0, 0, 0, 0.3);
  }

  .coupon-cutout-right {
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    box-shadow: inset -2px 0 4px rgba(0, 0, 0, 0.3);
    display: none;
  }

  .code-label {
    font-size: 9.600000000000001px;
    font-weight: 600;
    color: rgba(255, 215, 0, 0.8);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 2.4000000000000004px;
  }

  .code-text {
    font-size: 20.8px;
    font-weight: 800;
    color: #ffd700;
    letter-spacing: 3px;
    text-shadow:
      0 0 20px rgba(255, 215, 0, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.3);
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  }

  .code-shimmer {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.15), transparent);
    animation: shimmerSweep 3s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes shimmerSweep {
    0%,
    100% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
  }

  .copy-button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    font-size: 13.600000000000001px;
    font-weight: 700;
    color: #1a1a2e;
    background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
    border: none;
    border-radius: 0 8px 8px 0;
    cursor: pointer;
    white-space: normal;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .button-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 50%);
    pointer-events: none;
  }

  .copy-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(255, 215, 0, 0.5);
  }

  .copy-button:active {
    transform: translateY(0) scale(0.98);
  }

  .copy-icon,
  .check-icon,
  .arrow-icon {
    width: 16px;
    height: 16px;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }

  .check-icon {
    color: #22c55e;
  }

  .arrow-icon {
    opacity: 0.7;
  }

  .copy-button span {
    position: relative;
    z-index: 1;
  }

  /* Countdown timer */
  .countdown-time {
    font-size: 24px;
    font-weight: 700;
    color: white;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: 2px;
    margin-bottom: 4px;
    transition:
      color 0.3s ease,
      text-shadow 0.3s ease;
    /* Semi-transparent backdrop for visibility on any background */
    background: rgba(0, 0, 0, 0.3);
    padding: 4px 12px;
    border-radius: 6px;
  }

  .countdown-time.warning {
    color: #fbbf24;
    text-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
  }

  .countdown-time.critical {
    color: #ef4444;
    text-shadow: 0 0 25px rgba(239, 68, 68, 0.6);
    animation: timePulse 0.3s ease-in-out infinite;
  }

  @keyframes timePulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.02);
    }
  }

  .return-link {
    background: none;
    border: none;
    color: white;
    font-size: 13.600000000000001px;
    font-weight: 500;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.15s ease;
    text-decoration: none;
    flex-shrink: 0;
  }
  .return-link i {
    display: none;
  }

  .return-link:hover {
    opacity: 1;
  }

  /* Progress bar at bottom */
  .progress-bar-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(0, 0, 0, 0.3);
    z-index: 10;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: #4ade80;
    transition:
      width 0.05s linear,
      background-color 0.3s ease;
    box-shadow: 0 0 8px rgba(74, 222, 128, 0.6);
  }

  .progress-bar-container.warning .progress-bar-fill {
    background: #fbbf24;
    box-shadow: 0 0 8px rgba(251, 191, 36, 0.6);
  }

  .progress-bar-container.critical .progress-bar-fill {
    background: #ef4444;
    box-shadow: 0 0 10px rgba(239, 68, 68, 0.8);
    animation: progressPulse 0.5s ease-in-out infinite;
  }

  @keyframes progressPulse {
    0%,
    100% {
      box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
    }
    50% {
      box-shadow: 0 0 15px rgba(239, 68, 68, 1);
    }
  }

  /* Urgency screen effects */
  .offer-screen.urgency-warning {
    animation: screenWarning 2s ease-in-out infinite;
  }

  .offer-screen.urgency-critical {
    animation:
      screenCritical 1s ease-in-out infinite,
      urgentShake 0.15s ease-in-out infinite;
  }

  @keyframes urgentShake {
    0%,
    100% {
      transform: translate(0, 0) rotate(0deg);
    }
    15% {
      transform: translate(-2px, 0) rotate(-0.3deg);
    }
    30% {
      transform: translate(2px, 1px) rotate(0.3deg);
    }
    45% {
      transform: translate(-1px, -1px) rotate(-0.2deg);
    }
    60% {
      transform: translate(1px, 0) rotate(0.2deg);
    }
    75% {
      transform: translate(-2px, 1px) rotate(-0.3deg);
    }
    90% {
      transform: translate(1px, -1px) rotate(0.1deg);
    }
  }

  @keyframes screenWarning {
    0%,
    100% {
      background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    }
    50% {
      background: linear-gradient(135deg, var(--bg-color) 0%, #cc7700 100%);
    }
  }

  @keyframes screenCritical {
    0%,
    100% {
      background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    }
    50% {
      background: linear-gradient(135deg, #ff6b6b 0%, var(--bg-color-dark) 100%);
    }
  }

  /* Critical urgency vignette */
  .urgency-vignette {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 5;
    background: radial-gradient(ellipse at center, transparent 40%, rgba(239, 68, 68, 0.15) 70%, rgba(239, 68, 68, 0.4) 100%);
    animation: vignettePulse 0.5s ease-in-out infinite;
  }

  @keyframes vignettePulse {
    0%,
    100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }

  /* Wide layout (1200x200) - same as 800x400 but smaller typography */
  @container (min-aspect-ratio: 3/1) {
    .offer-screen {
      padding: 8px 16px;
    }

    .offer-content {
      gap: 6.4px;
    }

    .offer-header {
      gap: 2.4000000000000004px;
    }

    .countdown-time {
      font-size: 14.4px;
      letter-spacing: 1px;
      margin-bottom: 2.4000000000000004px;
    }

    .game-emoji {
      font-size: 16px;
      height: 20px;
    }

    .kicker-text {
      font-size: 9.600000000000001px;
      letter-spacing: 1px;
    }

    .offer-title {
      font-size: 15.200000000000001px;
    }

    .coupon-code {
      padding: 6.4px 16px;
    }

    .code-label {
      font-size: 8px;
      letter-spacing: 1px;
    }

    .code-text {
      font-size: 15.200000000000001px;
      letter-spacing: 2px;
    }

    .coupon-cutout {
      width: 12px;
      height: 12px;
    }

    .coupon-cutout-left {
      left: -6px;
    }

    .copy-button {
      padding: 6.4px 12px;
      font-size: 10.4px;
      gap: 4.800000000000001px;
    }

    .copy-icon,
    .check-icon,
    .arrow-icon {
      width: 12px;
      height: 12px;
    }

    .return-link {
      position: absolute;
      bottom: 8px;
      right: 12px;
      font-size: 10.4px;
      margin-top: 0;
    }
  }

  /* Narrow layout */
  @container (max-width: 500px) {
    .countdown-time {
      font-size: 20px;
    }

    .coupon-wrapper {
      flex-direction: column;
    }

    .coupon-code {
      border-radius: 8px 8px 0 0;
      border-right: 2px dashed rgba(255, 215, 0, 0.5);
      border-bottom: none;
    }

    .coupon-cutout-left {
      display: none;
    }

    .copy-button {
      border-radius: 0 0 8px 8px;
      justify-content: center;
    }

    .offer-title {
      font-size: 17.6px;
    }

    .return-link {
      font-size: 12.8px;
    }
  }

  /* Medium height + narrow width (250-350px height, <=500px width) */
  @container offer-screen (min-height: 250px) and (max-height: 349px) and (max-width: 500px) {
    .offer-screen {
      padding: 12px 16px;
      gap: 10px;
    }

    .offer-content {
      gap: 8px;
    }

    .offer-header {
      gap: 2px;
    }

    .countdown-time {
      font-size: 16px;
      padding: 3px 10px;
    }

    .game-emoji {
      font-size: 24px;
    }

    .kicker-text {
      font-size: 10px;
    }

    .offer-title {
      font-size: 16px;
      line-height: 1.2;
    }

    .coupon-code {
      padding: 8px 16px;
    }

    .code-label {
      font-size: 8px;
    }

    .code-text {
      font-size: 16px;
      letter-spacing: 2px;
    }

    .copy-button {
      padding: 8px 14px;
      font-size: 12px;
    }

    .return-link {
      font-size: 11px;
    }
  }

  /* Short AND narrow containers (height 0-200px, width < 800px) - compact styles */
  @container offer-screen (max-height: 200px) and (max-width: 799px) {
    .offer-screen {
      padding: 8px 16px;
    }

    .offer-content {
      gap: 6.4px;
    }

    .offer-header {
      gap: 2.4000000000000004px;
    }

    .countdown-time {
      font-size: 14.4px;
      letter-spacing: 1px;
      margin-bottom: 1.6px;
    }

    .game-emoji {
      font-size: 18px;
    }

    .kicker-text {
      font-size: 9.600000000000001px;
      letter-spacing: 1px;
    }

    .offer-title {
      font-size: 15.200000000000001px;
    }

    /* Keep coupon wrapper as row */
    .coupon-wrapper {
      flex-direction: row;
    }

    .coupon-code {
      padding: 6.4px 16px;
      border-radius: 8px 0 0 8px;
      border-right: none;
      border-bottom: 2px dashed rgba(255, 215, 0, 0.5);
    }

    .code-label {
      font-size: 8px;
      letter-spacing: 1px;
      margin-bottom: 1.6px;
    }

    .code-text {
      font-size: 15.200000000000001px;
      letter-spacing: 2px;
    }

    .coupon-cutout {
      width: 12px;
      height: 12px;
    }

    .coupon-cutout-left {
      left: -6px;
      display: block;
    }

    .copy-button {
      padding: 6.4px 12px;
      font-size: 11.200000000000001px;
      gap: 4.800000000000001px;
      border-radius: 0 8px 8px 0;
    }

    .copy-icon,
    .check-icon,
    .arrow-icon {
      width: 12px;
      height: 12px;
    }

    .return-link {
      font-size: 11.200000000000001px;
      margin-top: 2.4000000000000004px;
    }
  }

  /* Ultra-short wide layout (height <= 150px, width >= 800px) - two column layout */
  @container offer-screen (min-aspect-ratio: 3/1) {
    .offer-screen {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 6px 20px;
      position: relative !important;
    }

    .offer-content {
      display: flex !important;
      flex-direction: row !important;
      gap: 0 !important;
      max-width: none !important;
      width: 100% !important;
      flex: 1 !important;
      justify-content: flex-end !important;
      align-items: stretch !important;
      position: static !important;
      transform: none !important;
    }

    /* Left column: 50% width, vertically centered within entire offer screen */
    .offer-header {
      display: flex !important;
      flex-direction: column !important;
      gap: 2px !important;
      align-items: center !important;
      justify-content: center !important;
      position: absolute !important;
      top: 0 !important;
      bottom: 0 !important;
      left: 0 !important;
      width: 50% !important;
      padding: 0 20px !important;
      box-sizing: border-box !important;
      transform: none !important;
    }

    .countdown-time {
      font-size: 14px !important;
      padding: 4px 14px !important;
      margin: 0 !important;
    }

    .game-emoji {
      font-size: 22px !important;
      display: block;
      height: auto;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2)) !important;
    }

    .kicker-text {
      font-size: 11px !important;
      letter-spacing: 2px !important;
      font-weight: 700 !important;
    }

    .offer-title {
      font-size: 18px !important;
      font-weight: 800 !important;
      text-align: center;
      text-shadow:
        0 2px 8px rgba(0, 0, 0, 0.3),
        0 0 20px rgba(255, 255, 255, 0.15) !important;
      letter-spacing: 0.5px !important;
    }

    /* Right column: 50% width, vertically centered within entire offer screen */
    .copy-section {
      display: flex !important;
      flex-direction: column !important;
      gap: 6px !important;
      position: absolute !important;
      top: 0 !important;
      bottom: 0 !important;
      right: 0 !important;
      width: 50% !important;
      justify-content: center !important;
      align-items: center !important;
      padding: 0 20px !important;
      box-sizing: border-box !important;
      transform: none !important;
    }

    .coupon-wrapper {
      flex-direction: row;
      width: auto;
      position: relative !important;
    }

    .coupon-cutout {
      display: none !important;
    }

    .coupon-code {
      padding: 14px 28px !important;
      box-shadow: inset 0 0 20px rgba(255, 215, 0, 0.1) !important;
    }

    .code-label {
      font-size: 9px !important;
      margin-bottom: 3px !important;
      letter-spacing: 2px !important;
    }

    .code-text {
      font-size: 20px !important;
      letter-spacing: 3px !important;
      text-shadow: 0 0 15px rgba(255, 215, 0, 0.4) !important;
    }

    .copy-button {
      padding: 14px 22px !important;
      font-size: 14px !important;
      font-weight: 700 !important;
      gap: 6px;
      box-shadow: 0 4px 20px rgba(255, 215, 0, 0.4) !important;
      min-width: 200px !important;
    }

    .copy-icon,
    .check-icon,
    .arrow-icon {
      width: 14px;
      height: 14px;
    }

    /* Bottom-right corner of component */
    .return-link {
      position: absolute !important;
      bottom: 8px !important;
      right: 12px !important;
      left: auto !important;
      top: auto !important;
      font-size: 9px !important;
      margin: 0 !important;
      z-index: 100;
    }

    .progress-bar-container {
      height: 3px;
    }
  }

  /* Narrow compact layout - stack copy button below code */
  @container offer-screen (max-width: 1199px) and (min-aspect-ratio: 3/1) {
    .coupon-wrapper {
      flex-direction: column !important;
      width: 100% !important;
    }

    .coupon-code {
      border-radius: 8px 8px 0 0 !important;
      border-right: 2px dashed rgba(255, 215, 0, 0.5) !important;
      border-bottom: none !important;
    }

    .copy-button {
      border-radius: 0 0 8px 8px !important;
      justify-content: center !important;
      min-width: unset !important;
    }
  }
</style>
